/**
 * The line under a repository search that says whose GitHub installation the
 * list comes from and how much of it the YAGNI app was given, with the
 * GitHub page where that is changed (YAG-1064). A person who sees
 * repositories they did not pick learns here that the app was given every
 * repository on the account, or a different account's.
 */

/** What `GET /api/repos/available` says about the installation beside its repositories. */
export interface RepoScope {
  /** The GitHub account the installation belongs to. */
  account: string | null;
  /** "all": every repository on the account; "selected": the ones the customer ticked. */
  repositorySelection: "all" | "selected" | null;
  /** The GitHub page where the selection is changed. */
  settingsUrl: string | null;
  /** How many repositories GitHub lists for the installation. */
  totalCount: number | null;
}

/** The scope from an `/api/repos/available` body; null when it names none (an older backend). */
export function readRepoScope(body: unknown): RepoScope | null {
  if (typeof body !== "object" || body === null) return null;
  const raw = body as Record<string, unknown>;
  const account = typeof raw.account === "string" && raw.account ? raw.account : null;
  const repositorySelection =
    raw.repositorySelection === "all" || raw.repositorySelection === "selected"
      ? raw.repositorySelection
      : null;
  const settingsUrl =
    typeof raw.settingsUrl === "string" && raw.settingsUrl.startsWith("https://github.com/")
      ? raw.settingsUrl
      : null;
  const totalCount =
    typeof raw.totalCount === "number" && Number.isFinite(raw.totalCount) ? raw.totalCount : null;
  if (!account && !repositorySelection && !settingsUrl) return null;
  return { account, repositorySelection, settingsUrl, totalCount };
}

/** The sentence alone, so a test or another surface can say the same thing. */
export function repoScopeSentence(scope: RepoScope, listed: number): string {
  const on = scope.account ? ` on ${scope.account}` : "";
  if (scope.repositorySelection === "all") {
    return `The YAGNI app sees every repository${scope.account ? on : " on this GitHub account"}.`;
  }
  const count = scope.totalCount ?? listed;
  return `${count} ${count === 1 ? "repository" : "repositories"} the YAGNI app was given${on}.`;
}

export function RepoScopeLine({
  scope,
  listed,
  testId,
}: {
  scope: RepoScope | null;
  /** How many repositories the picker lists, when GitHub gave no count. */
  listed: number;
  testId: string;
}) {
  if (!scope) return null;
  const linkText =
    scope.repositorySelection === "all" ? "Choose fewer on GitHub" : "Change on GitHub";
  return (
    <div className="help" data-testid={testId}>
      {repoScopeSentence(scope, listed)}
      {scope.settingsUrl && (
        <>
          {" "}
          <a
            href={scope.settingsUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`${testId}-link`}
          >
            {linkText}
          </a>
          .
        </>
      )}
    </div>
  );
}

/** Picks every repository the search matches at once; nothing to show for one match. */
export function RepoSelectAll({
  count,
  testId,
  onSelect,
}: {
  count: number;
  testId: string;
  onSelect: () => void;
}) {
  if (count < 2) return null;
  return (
    <button
      type="button"
      className="ref"
      style={{ alignSelf: "flex-start", margin: "2px 8px" }}
      data-testid={testId}
      onClick={onSelect}
    >
      Select all {count}
    </button>
  );
}

/** The matches past what a picker lists, and how to reach them. */
export function RepoMoreMatches({ hidden }: { hidden: number }) {
  if (hidden <= 0) return null;
  return (
    <span className="fact" style={{ padding: "2px 8px" }}>
      and {hidden} more · type to narrow
    </span>
  );
}
